import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Lock, Bell, Trash2, Upload, X, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function Settings() {
  const { user, token, updateUser } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('profile');

  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  // Notification preferences state
  const [notifyTaskCompletions, setNotifyTaskCompletions] = useState(true);
  const [notifyWeeklyDigest, setNotifyWeeklyDigest] = useState(true);
  const [notifyTipsUpdates, setNotifyTipsUpdates] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);

  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Sync state with user when user changes
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setAvatarUrl(user.avatar_url || '');
    }
  }, [user]);

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Load notification preferences
  useEffect(() => {
    const loadNotificationPreferences = async () => {
      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/user/notifications`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json() as {
          success: boolean;
          data?: { task_completions: number; weekly_digest: number; tips_updates: number };
        };

        if (data.success && data.data) {
          setNotifyTaskCompletions(Boolean(data.data.task_completions));
          setNotifyWeeklyDigest(Boolean(data.data.weekly_digest));
          setNotifyTipsUpdates(Boolean(data.data.tips_updates));
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      } finally {
        setLoadingNotifications(false);
      }
    };

    loadNotificationPreferences();
  }, [token]);

  // Save notification preferences
  const saveNotificationPreference = async (key: string, value: boolean) => {
    if (!token) return;

    setSavingNotifications(true);
    try {
      const response = await fetch(`${API_URL}/user/notifications`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          [key]: value,
        }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        setMessage({ type: 'success', text: 'Notification preference updated' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update preference' });
      }
    } catch (error) {
      console.error('Failed to save notification preference:', error);
      setMessage({ type: 'error', text: 'Failed to update preference' });
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleNotificationToggle = (key: string, currentValue: boolean, setter: (value: boolean) => void) => {
    const newValue = !currentValue;
    setter(newValue);
    saveNotificationPreference(key, newValue);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' });
      return;
    }

    // Validate file size (500KB)
    if (file.size > 500 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 500KB' });
      return;
    }

    setUploadingAvatar(true);

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setAvatarPreview(base64);
      setUploadingAvatar(false);
    };
    reader.onerror = () => {
      setMessage({ type: 'error', text: 'Failed to read image file' });
      setUploadingAvatar(false);
    };
    reader.readAsDataURL(file);

    // Reset input
    e.target.value = '';
  };

  const removeAvatar = () => {
    setAvatarPreview(null);
    setAvatarUrl('');
  };

  const saveProfile = async () => {
    if (!token) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/user/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          avatar_url: avatarPreview || avatarUrl || null,
        }),
      });

      const data = await response.json() as { success: boolean; data?: { name: string; avatar_url: string }; error?: string };

      if (data.success && data.data) {
        // Update local auth store
        updateUser({
          name: data.data.name,
          avatar_url: data.data.avatar_url,
        });
        setAvatarUrl(data.data.avatar_url || '');
        setAvatarPreview(null);
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const updatePassword = async () => {
    if (!token) return;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }

    setUpdatingPassword(true);
    try {
      const response = await fetch(`${API_URL}/user/password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        setMessage({ type: 'success', text: 'Password updated successfully!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update password' });
      }
    } catch (error) {
      console.error('Failed to update password:', error);
      setMessage({ type: 'error', text: 'Failed to update password. Please try again.' });
    } finally {
      setUpdatingPassword(false);
    }
  };

  const displayAvatar = avatarPreview || avatarUrl;

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Message Toast */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-xl flex items-center gap-3 shadow-lg ${
              message.type === 'success'
                ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                : 'bg-red-500/10 border border-red-500/20 text-red-400'
            }`}
          >
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} className="ml-2">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div variants={fadeInUp}>
        <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Settings</h1>
        <p className={isDark ? 'text-text-secondary' : 'text-slate-500'}>Manage your account settings</p>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={fadeInUp} className={`flex space-x-1 rounded-xl p-1 w-fit ${isDark ? 'bg-dark-800' : 'bg-slate-100'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-gradient-accent text-white'
                : isDark ? 'text-text-secondary hover:text-white' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </motion.div>

      {/* Hidden file input */}
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={handleAvatarChange}
      />

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <motion.div variants={fadeInUp} className="max-w-2xl space-y-6">
          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Profile Information</h3>

            {/* Avatar */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative">
                {displayAvatar ? (
                  <div className="relative">
                    <img
                      src={displayAvatar}
                      alt="Avatar"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                    <button
                      onClick={removeAvatar}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-accent flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                )}
                {uploadingAvatar && (
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="btn-secondary flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Change Avatar
              </button>
            </div>

            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Full Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-field"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  className="input-field"
                  disabled
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                  Email cannot be changed
                </p>
              </div>
            </div>

            <button
              onClick={saveProfile}
              disabled={saving}
              className="btn-primary mt-6 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>

          {/* Danger Zone */}
          <div className={`rounded-2xl p-6 border border-accent-red/30 ${isDark ? 'bg-dark-800' : 'bg-white shadow-sm'}`}>
            <h3 className="text-lg font-semibold text-accent-red mb-4">Danger Zone</h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <button className="flex items-center space-x-2 px-4 py-2 bg-accent-red/10 text-accent-red rounded-xl hover:bg-accent-red/20 transition-colors">
              <Trash2 className="w-4 h-4" />
              <span>Delete Account</span>
            </button>
          </div>
        </motion.div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <motion.div variants={fadeInUp} className="max-w-2xl space-y-6">
          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Change Password</h3>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input-field"
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
            <button
              onClick={updatePassword}
              disabled={updatingPassword}
              className="btn-primary mt-6 flex items-center gap-2"
            >
              {updatingPassword ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </button>
          </div>

          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Two-Factor Authentication</h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
              Add an extra layer of security to your account
            </p>
            <button className="btn-secondary">Enable 2FA</button>
          </div>
        </motion.div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <motion.div variants={fadeInUp} className="max-w-2xl">
          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Email Notifications</h3>
            {loadingNotifications ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-accent-purple" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Task completions */}
                <div className={`flex items-center justify-between py-3 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Task completions</p>
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Get notified when tasks finish</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={notifyTaskCompletions}
                      onChange={() => handleNotificationToggle('task_completions', notifyTaskCompletions, setNotifyTaskCompletions)}
                      disabled={savingNotifications}
                    />
                    <div className={`w-11 h-6 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-purple ${isDark ? 'bg-dark-700' : 'bg-slate-300'} ${savingNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                  </label>
                </div>

                {/* Weekly digest */}
                <div className={`flex items-center justify-between py-3 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Weekly digest</p>
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Summary of your AI activity</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={notifyWeeklyDigest}
                      onChange={() => handleNotificationToggle('weekly_digest', notifyWeeklyDigest, setNotifyWeeklyDigest)}
                      disabled={savingNotifications}
                    />
                    <div className={`w-11 h-6 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-purple ${isDark ? 'bg-dark-700' : 'bg-slate-300'} ${savingNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                  </label>
                </div>

                {/* Tips & updates */}
                <div className={`flex items-center justify-between py-3 ${isDark ? 'border-border' : 'border-slate-200'}`}>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Tips & updates</p>
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Product news and tips</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={notifyTipsUpdates}
                      onChange={() => handleNotificationToggle('tips_updates', notifyTipsUpdates, setNotifyTipsUpdates)}
                      disabled={savingNotifications}
                    />
                    <div className={`w-11 h-6 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-purple ${isDark ? 'bg-dark-700' : 'bg-slate-300'} ${savingNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                  </label>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
